import React from 'react';
import { supabase, BUILD } from '../lib/supabase';
import { OnHandLogo } from '../components/brand/OnHandLogo';
import { SIGNIN_ART } from '../components/brand/artwork';

/*
  Setting a new password, from the link in the email.

  **Built 10 September 2026**, to the approved drawing in `docs/design/login/`,
  which is where `reset_card` in its `build.py` holds the wording. Group A drew
  this on 28 August 2026 and until today it was the only one of the sign-in
  screens with nothing behind it.

  **Why it could not exist before.** A reset works exactly one way: the person
  types their email, OnHand sends a one-time link, and following the link is what
  proves it is them. With nothing able to send email there was no link, so there
  was no reset, only an administrator setting a password by hand.

  **What this fixes, and it is bigger than a convenience.**
  `docs/BEFORE-ANYONE-ELSE-GETS-ACCESS.md` records that every account in OnHand
  was created with the same password and **nobody can change their own**. This is
  how somebody changes their own.

  **How a person gets here.** Supabase puts a one-time token in the address of
  the link and announces `PASSWORD_RECOVERY` when the page opens with it. There
  is a real, signed-in session at that moment, and the only thing it is for is
  setting the password, so this screen covers everything until that is done.

  **Restyled 11 September 2026 to the brand guide**, job 27 phase 4. It shares
  the sign-in screen's card, canvas and artwork, and it always did: the ground
  rule for this rollout is that changing something shared means changing every
  place that uses it in the same phase, and this is the other place. Nothing
  about what it does changed, only how it looks and the two field labels.
*/

export default function SetNewPassword({ email }) {
  const [pw, setPw] = React.useState('');
  const [again, setAgain] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const strength = measure(pw);
  const longEnough = pw.length >= 10;
  const matches = again.length > 0 && again === pw;
  const canSave = longEnough && matches && !busy;

  async function submit(e) {
    e.preventDefault();
    setError(null);
    /*
      **Checked here as well as on the button**, because a form submits on Enter
      whatever the button is doing, and a save that goes through with a password
      the person did not mean to type is not recoverable: the old one is gone.
    */
    if (!longEnough) { setError('That is under 10 characters.'); return; }
    if (!matches) { setError('Those two do not match.'); return; }

    setBusy(true);
    let err = null;
    try {
      ({ error: err } = await supabase.auth.updateUser({ password: pw }));
    } catch (e2) {
      err = e2;
    }
    setBusy(false);
    /*
      **It stays here when the write failed.** Sending somebody on to the app on
      a refused change would leave them believing their password is one thing
      while it is still another, and they would find out at the next sign-in with
      no idea why.
    */
    if (err) {
      setError(String(err.message || err).includes('same')
        ? 'That is the password you already had. Choose a different one.'
        : 'That could not be saved. The link may have expired, in which case ask for a new one.');
      return;
    }
    /*
      **Straight into the app, not back to the sign-in screen.** Following the
      link already signed them in; sending them to type the password they have
      only just chosen would be OnHand asking a question it knows the answer to.
    */
    window.location.replace('/');
  }

  return (
    <div className="login-page" data-build={BUILD}>
      <div className="login-wallpaper" aria-hidden="true" style={SIGNIN_ART} />

      <div className="signin-card oh-glass">
        <div className="signin-logo">
          <OnHandLogo iconSize={40} />
        </div>

        <div className="signin-head">
          <h1 className="signin-title">Set a new password.</h1>
          <p className="signin-sub">
            You are setting a new password for{' '}
            <b>{email || 'your account'}</b>.
          </p>
        </div>

        <form className="signin-form" onSubmit={submit}>
          <div className="signin-fields">
            <div className="signin-field">
              <label className="fld-label" htmlFor="reset-new">New password</label>
              <input
                id="reset-new" className="fld"
                type="password" value={pw} autoComplete="new-password" autoFocus
                aria-describedby="reset-hint"
                onChange={(e) => setPw(e.target.value)}
              />
              {/* Three bars, the drawing's own. It says how long rather than
                  demanding a capital and a symbol, because length is what
                  actually makes a password hard to guess and the rest makes it
                  hard to remember, which is how a password ends up on a sticky
                  note. */}
              <div className="reset-bars" aria-hidden="true">
                {[0, 1, 2].map(i => (
                  <div key={i} className={'reset-bar' + (i < strength ? ' reset-bar-on' : '')} />
                ))}
              </div>
              <div className="reset-hint" id="reset-hint">
                At least 10 characters. Longer beats complicated.
              </div>
            </div>

            <div className="signin-field">
              <label className="fld-label" htmlFor="reset-again">Repeat it</label>
              <input
                id="reset-again" className="fld"
                type="password" value={again} autoComplete="new-password"
                aria-invalid={again.length > 0 && !matches ? 'true' : undefined}
                onChange={(e) => setAgain(e.target.value)}
              />
            </div>
          </div>

          {error && (
            <div className="signin-msg signin-msg-wrong" role="alert">{error}</div>
          )}

          <button type="submit" className="btn btn-lg btn-primary signin-submit" disabled={!canSave}>
            {busy ? 'Saving...' : 'Save and sign in'}
          </button>
        </form>

        <div className="signin-foot">
          <div className="signin-note">This link works once and expires in an hour.</div>
          <div className="signin-build">Build {BUILD}</div>
        </div>
      </div>
    </div>
  );
}


/*
  How strong, in three bars.

  **Length, and then two small things that are actually true.** A ten character
  password fills one bar, fourteen fills two, and eighteen fills three; a mixture
  of kinds of character brings the higher bars forward a little. It deliberately
  does not demand a capital and a symbol: that makes a password hard to remember
  without making it much harder to guess, which is how one ends up written on a
  note stuck to a monitor.
*/
function measure(pw) {
  if (pw.length < 10) return 0;
  const kinds = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter(r => r.test(pw)).length;
  const score = pw.length + (kinds - 1) * 2;
  if (score >= 18) return 3;
  if (score >= 14) return 2;
  return 1;
}
